import { SearchEngine, wordMatch } from '../../../search/SearchEngine.js';
import type { SearchEntry, SearchIndexFile } from '../../../search/SearchIndexFormat.js';
import { searchTokens } from '../../../search/SearchText.js';

// Site search panel (_common_/views/templates/layout/site-search/). The index of the page's
// language is fetched once, on the first opening, and searched here: no query is sent anywhere.
// Opening pushes a history entry, so the phone's Back button closes the panel instead of
// leaving the page; choosing a result replaces that entry with the result page.

const RESULT_LIMIT = 24;
const WORD = /([\p{L}\p{N}'’ʼ]+)/u;

interface Elements {
    dialog: HTMLDialogElement;
    input: HTMLInputElement;
    status: HTMLElement;
    results: HTMLUListElement;
    resultTemplate: HTMLTemplateElement;
}

function findElements(dialog: HTMLDialogElement): Elements {
    return {
        dialog,
        input: dialog.querySelector<HTMLInputElement>('[data-site-search-input]')!,
        status: dialog.querySelector<HTMLElement>('[data-site-search-status]')!,
        results: dialog.querySelector<HTMLUListElement>('[data-site-search-results]')!,
        resultTemplate: dialog.querySelector<HTMLTemplateElement>('template[data-site-search-result]')!,
    };
}

/** Title text with the words matching the query marked (<mark>). */
function markedTitle(title: string, queryWords: string[]): DocumentFragment {
    const fragment = document.createDocumentFragment();
    for (const part of title.split(WORD)) {
        const folded = searchTokens(part)[0];
        if (folded && queryWords.some((word) => wordMatch(word, folded) > 0)) {
            const mark = document.createElement('mark');
            mark.textContent = part;
            fragment.append(mark);
        } else if (part) {
            fragment.append(part);
        }
    }
    return fragment;
}

export class SiteSearch {
    private readonly elements: Elements;
    private engine?: Promise<SearchEngine>;
    private entries: SearchEntry[] = [];
    private active = -1;
    private opener?: HTMLElement;
    // Closing because a result was chosen: the history entry is replaced, not popped.
    private navigating = false;

    constructor(dialog: HTMLDialogElement) {
        this.elements = findElements(dialog);
        const { input, results } = this.elements;

        dialog.querySelector('[data-site-search-close]')!.addEventListener('click', () => dialog.close());
        dialog.querySelector('[data-site-search-form]')!.addEventListener('submit', (event) => {
            event.preventDefault();
            this.choose(Math.max(this.active, 0));
        });
        // A click on the backdrop (outside the panel) closes, as Escape does.
        dialog.addEventListener('click', (event) => {
            if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => this.onClose());
        input.addEventListener('input', () => void this.update());
        input.addEventListener('keydown', (event) => this.onKeydown(event));
        results.addEventListener('click', (event) => {
            const option = (event.target as Element).closest<HTMLElement>('[role="option"]');
            const mouse = event as MouseEvent;
            // New tab or window: leave it to the browser.
            if (!option || mouse.button !== 0 || mouse.ctrlKey || mouse.metaKey || mouse.shiftKey || mouse.altKey)
                return;
            event.preventDefault();
            this.choose(Number(option.dataset.index));
        });
        window.addEventListener('popstate', () => {
            if (dialog.open) dialog.close();
        });
    }

    open(opener: HTMLElement, query = ''): void {
        const { dialog, input } = this.elements;
        if (dialog.open) return;
        this.opener = opener;
        if (opener.matches('button')) opener.setAttribute('aria-expanded', 'true');
        dialog.showModal();
        history.pushState({ siteSearch: true }, '');
        input.value = query;
        // Synchronously in the click: the only way a phone opens its keyboard for a focus() call.
        input.focus();
        input.setSelectionRange(query.length, query.length);
        this.followVisualViewport();
        void this.update();
    }

    // The close event comes after dialog.close() returns, also after choose() started the navigation.
    private onClose(): void {
        const { input } = this.elements;
        const navigating = this.navigating;
        this.navigating = false;
        if (this.opener?.matches('button')) this.opener.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-expanded', 'false');
        window.visualViewport?.removeEventListener('resize', this.resizeToViewport);
        if (navigating) return;
        if (history.state?.siteSearch) history.back();
        this.opener?.focus({ preventScroll: true });
    }

    // The on-screen keyboard shrinks the visual viewport only; the panel follows it, so the
    // results stay scrollable above the keyboard.
    private readonly resizeToViewport = (): void => {
        const viewport = window.visualViewport;
        if (viewport) this.elements.dialog.style.setProperty('--site-search-height', `${viewport.height}px`);
    };

    private followVisualViewport(): void {
        this.resizeToViewport();
        window.visualViewport?.addEventListener('resize', this.resizeToViewport);
    }

    private loadEngine(): Promise<SearchEngine> {
        this.engine ??= fetch(this.elements.dialog.dataset.index!, { credentials: 'same-origin' })
            .then((response) => {
                if (!response.ok) throw new Error(`search index: HTTP ${response.status}`);
                return response.json() as Promise<SearchIndexFile>;
            })
            .then((file) => new SearchEngine(file.entries))
            .catch((error: unknown) => {
                this.engine = undefined;
                throw error;
            });
        return this.engine;
    }

    private async update(): Promise<void> {
        const { input, status } = this.elements;
        const query = input.value;
        let engine: SearchEngine;
        try {
            engine = await this.loadEngine();
        } catch {
            this.render([], []);
            status.textContent = status.dataset.loadError!;
            return;
        }
        // A newer keystroke already rendered its own results.
        if (input.value !== query) return;

        const queryWords = searchTokens(query).filter((word) => word.length >= 2);
        const entries = queryWords.length ? engine.search(query, RESULT_LIMIT) : [];
        this.render(entries, queryWords);
        if (!queryWords.length) status.textContent = status.dataset.hint!;
        else if (!entries.length) status.textContent = status.dataset.noResults!.replace('{query}', query.trim());
        else status.textContent = '';
    }

    private render(entries: SearchEntry[], queryWords: string[]): void {
        const { input, results, resultTemplate } = this.elements;
        this.entries = entries;
        this.active = -1;
        input.removeAttribute('aria-activedescendant');
        results.replaceChildren(
            ...entries.map((entry, index) => {
                const item = (resultTemplate.content.firstElementChild as HTMLElement).cloneNode(true) as HTMLElement;
                // The link is the option: a link inside an option would nest interactive controls.
                const option = item.querySelector<HTMLAnchorElement>('[role="option"]')!;
                option.id = `site-search-option-${index}`;
                option.dataset.index = String(index);
                option.href = entry.url;
                const thumb = option.querySelector<HTMLImageElement>('.site-search__thumb')!;
                if (entry.thumbnail) {
                    thumb.src = entry.thumbnail.src;
                    thumb.width = entry.thumbnail.width;
                    thumb.height = entry.thumbnail.height;
                } else {
                    thumb.remove();
                }
                option.querySelector('.site-search__kind')!.textContent = entry.kind;
                option
                    .querySelector('.site-search__result-title')!
                    .replaceChildren(markedTitle(entry.title, queryWords));
                option.querySelector('.site-search__description')!.textContent = entry.description;
                return item;
            }),
        );
        results.hidden = entries.length === 0;
        results.scrollTop = 0;
        input.setAttribute('aria-expanded', String(entries.length > 0));
    }

    private onKeydown(event: KeyboardEvent): void {
        // In a search field Escape would only clear the text (Chromium) instead of closing the panel;
        // it closes only the panel, not also a drawer it was opened from (menu9 listens on the document).
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.elements.dialog.close();
            return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        if (!this.entries.length) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const count = this.entries.length;
        this.select(this.active < 0 ? (step > 0 ? 0 : count - 1) : (this.active + step + count) % count);
    }

    private select(index: number): void {
        const { input, results } = this.elements;
        results.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
        this.active = index;
        const option = results.querySelectorAll<HTMLElement>('[role="option"]')[index];
        option.setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
    }

    private choose(index: number): void {
        const entry = this.entries[index];
        if (!entry) return;
        this.navigating = true;
        const replaceSearchEntry = history.state?.siteSearch === true;
        this.elements.dialog.close();
        if (replaceSearchEntry) location.replace(entry.url);
        else location.assign(entry.url);
    }
}

/** Connects the page's search dialog with its openers: header buttons and search fields (e.g. PrawkoStart HOME). */
export function initSiteSearch(): void {
    const dialog = document.querySelector<HTMLDialogElement>('dialog[data-site-search]');
    if (!dialog) return;
    const search = new SiteSearch(dialog);

    for (const button of document.querySelectorAll<HTMLButtonElement>('button[data-site-search-open]')) {
        button.setAttribute('aria-expanded', 'false');
        button.addEventListener('click', () => search.open(button));
    }
    // A field on the page hands what was typed to the panel: tapping it or typing opens the panel.
    for (const form of document.querySelectorAll<HTMLFormElement>('form[data-site-search-field]')) {
        const field = form.querySelector<HTMLInputElement>('input')!;
        const openWithField = () => {
            const query = field.value;
            field.value = '';
            search.open(field, query);
        };
        field.addEventListener('click', openWithField);
        field.addEventListener('input', openWithField);
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            openWithField();
        });
    }
}
